import EmailInput from 'components/auth/email-input'
import PasswordInput from 'components/auth/password-input'
import { WarningIcon } from 'components/icons'
import Button from 'components/inputs/button'
import Form from 'components/inputs/form'
import Input from 'components/inputs/input'
import { useCurrentUser } from 'context/current-user-context'
import classNames from 'helpers/class-names'
import { useStorage } from 'hooks/use-storage'
import { signIn } from 'next-auth/react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FormEvent, useRef, useState } from 'react'
import { Panel, Stack } from 'ui'

const CookieWarning = () => {
  return (
    <div className="mt-4 text-center text-sm text-slate-400">
      This extension requires cookies.
    </div>
  )
}

const IframeContentUnavailable = () => {
  return (
    <Stack vertical spacing={4}>
      <div
        className={classNames(
          'flex w-full rounded border-l-8 border-warning bg-white py-4 shadow-sm transition duration-150 ease-in-out dark:bg-gray-600',
        )}>
        <div className="flex-0 items-start self-stretch">
          <WarningIcon className="ml-2 mr-3 w-6 text-yellow" />
        </div>
        <div
          role="alert"
          className="flex-1 pr-4 text-left text-sm text-primary dark:text-gray-100">
          <strong className="font-bold">Content Unavailable</strong> <br />
          This extension requires cookie access to show you Toolpath content.
          Check your browser settings then refresh the page to try again.
        </div>
      </div>
    </Stack>
  )
}

const LoginForm = () => {
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [emailError, setEmailError] = useState<string>(null)
  const [passwordError, setPasswordError] = useState<string>(null)
  const { cookieAccessDenied, requestCookieAccess, isIframe, missingCookies } =
    useStorage()

  const ref = useRef<HTMLButtonElement>()

  const { refreshUser } = useCurrentUser()
  const router = useRouter()

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    ref.current.focus()
    setPasswordError(null)
    setEmailError(null)
    await requestCookieAccess()
  }

  const handleSuccess = async (
    response: { redirect: string },
    setIsSubmitting,
  ) => {
    setIsSubmitting(true)
    await signIn('credentials', {
      email,
      password,
      redirect: false,
    })
    refreshUser()
    router.push(response.redirect)
  }

  const handleError = async (errors) => {
    if (Array.isArray(errors)) {
      errors.forEach((errorMessage) => {
        if (errorMessage?.email) {
          setEmailError(errorMessage.email)
        }

        if (errorMessage?.password) {
          setPasswordError(errorMessage.password)
        }
      })
    } else {
      setPasswordError(errors)
    }
  }

  if (cookieAccessDenied) {
    return (
      <Panel>
        <div className="p-4">
          <IframeContentUnavailable />
        </div>
      </Panel>
    )
  }

  return (
    <Panel classNameInner="flex-col">
      <Form
        id="login-form"
        action={`/api/users/auth`}
        onSubmit={handleSubmit}
        onSuccess={handleSuccess}
        onError={handleError}
        className="w-full p-6 pt-5">
        {({ isSubmitting }) => (
          <>
            <EmailInput
              value={email}
              setValue={setEmail}
              name="email"
              id="email-input"
              label="Email Address"
              error={emailError}
              setError={setEmailError}
              disabled={isSubmitting}
            />
            <PasswordInput
              value={password}
              setValue={setPassword}
              name="password"
              autoComplete="current-password"
              id="password-input"
              label="Password"
              error={passwordError}
              setError={setPasswordError}
              disabled={isSubmitting}
            />
            <Input
              name="allowUnsecureIframe"
              id="allowUnsecureIframe"
              value={String(isIframe)}
              setValue={() => null}
              hidden
              className="hidden"
            />
            <div className="mt-1 text-right text-sm">
              <Link href="/auth/reset-password">
                <a className="mr-px text-info underline underline-offset-2 hover:no-underline focus-visible:rounded-md focus-visible:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/75">
                  Forgot your password?
                </a>
              </Link>
            </div>
            <Button
              ref={ref}
              type="submit"
              primary
              className="mt-5 w-full"
              disabled={isSubmitting || email === '' || password === ''}>
              {isSubmitting ? 'Authenticating...' : 'Log in'}
            </Button>
            <div className="mt-5 text-center text-sm">
              <Link href="/sign-up">
                <a className=" text-info underline underline-offset-2 hover:no-underline focus-visible:rounded-md focus-visible:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/75">
                  Don't have an account? Sign up.
                </a>
              </Link>
            </div>
            {isIframe && missingCookies && <CookieWarning />}
          </>
        )}
      </Form>
    </Panel>
  )
}

export default LoginForm
